import { resolveBrandPalette } from '@supply-mover-2-0/shared';
import type { CSSProperties } from 'react';
import { useMemo } from 'react';
import { usePortalSiteStore } from '../stores/portalSiteStore';

/**
 * The accent variables for a panel that is dark whatever the page's mode: the
 * sign-in card and the registration page under a dark login theme (PD-17036,
 * PD-17051).
 *
 * BrandThemeApplier sets the accent variables for the page's mode, so in light
 * mode a dark panel inherited the brand's LIGHT accent. For a brand with a deep
 * light accent (B&L's teal #005252) its links, its buttons and the resend-code
 * text measured about 2.3:1 against the panel. Setting these on the panel
 * re-points every accent inside it (buttons, links, focus rings, code boxes) at
 * the colors the brand chose for dark surfaces, worked out by the same
 * resolveBrandPalette the rest of the storefront uses.
 *
 * Returns undefined when the panel is not dark, so a light panel keeps the
 * page's own accent.
 */
export function useDarkPanelAccentStyle(
	isDarkPanel: boolean,
): CSSProperties | undefined {
	const primaryColor = usePortalSiteStore((s) => s.site?.primaryColor ?? null);
	const accentColorLight = usePortalSiteStore(
		(s) => s.site?.accentColorLight ?? null,
	);
	const accentColorDark = usePortalSiteStore(
		(s) => s.site?.accentColor ?? null,
	);

	return useMemo<CSSProperties | undefined>(() => {
		if (!isDarkPanel) {
			return undefined;
		}
		const dark = resolveBrandPalette(
			{ primaryColor, accentColorLight, accentColorDark },
			'dark',
		);
		return {
			'--color-portal-accent': dark.accent,
			'--color-portal-accent-hover': dark.accentHover,
			'--color-portal-accent-fg': dark.accentText,
			'--color-portal-accent-text': dark.accentLinkText,
		} as CSSProperties;
	}, [isDarkPanel, primaryColor, accentColorLight, accentColorDark]);
}
